import AssignmentService from './assignmentService';

class AssignmentTestService {
  constructor() {
    this.testResults = [];
    this.isDevelopmentMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  }

  // Test result structure
  createTestResult(testName, status, message, data = null, duration = 0) {
    return {
      testName,
      status, // 'pass', 'fail', 'error'
      message,
      data,
      duration,
      timestamp: new Date().toISOString()
    };
  }

  // Run all tests
  async runAllTests() {
    console.log('🧪 Starting Assignment Test Suite...');
    this.testResults = [];
    
    const startTime = Date.now();
    
    try {
      // API Tests
      await this.testGetAssignments();
      await this.testGetAssignmentStats();
      await this.testCreateAssignment();
      await this.testUpdateAssignment();
      await this.testDeleteAssignment();
      
      // Validation Tests
      await this.testFormValidations();
      await this.testDataIntegrity();
      
      // UI Tests
      await this.testUIComponents();
      
      const totalDuration = Date.now() - startTime;
      const passedTests = this.testResults.filter(r => r.status === 'pass').length;
      const failedTests = this.testResults.filter(r => r.status === 'fail').length;
      const errorTests = this.testResults.filter(r => r.status === 'error').length;
      
      console.log(`✅ Test Suite Completed in ${totalDuration}ms`);
      console.log(`📊 Results: ${passedTests} passed, ${failedTests} failed, ${errorTests} errors`);
      
      return {
        success: failedTests === 0 && errorTests === 0,
        totalTests: this.testResults.length,
        passed: passedTests,
        failed: failedTests,
        errors: errorTests,
        duration: totalDuration,
        results: this.testResults
      };
    } catch (error) {
      console.error('❌ Test Suite Failed:', error);
      return {
        success: false,
        error: error.message,
        results: this.testResults
      };
    }
  }

  // Test API: Get Assignments
  async testGetAssignments() {
    const testName = 'API: Get Assignments';
    const startTime = Date.now();
    
    try {
      const response = await AssignmentService.getAssignments();
      const duration = Date.now() - startTime;
      
      if (response.success && Array.isArray(response.data)) {
        this.testResults.push(this.createTestResult(
          testName,
          'pass',
          `Successfully fetched ${response.data.length} assignments`,
          { count: response.data.length, pagination: response.pagination },
          duration
        ));
      } else {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'Invalid response format',
          response,
          duration
        ));
      }
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `API call failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test API: Get Assignment Stats
  async testGetAssignmentStats() {
    const testName = 'API: Get Assignment Stats';
    const startTime = Date.now();
    
    try {
      const response = await AssignmentService.getAssignmentStats();
      const duration = Date.now() - startTime;
      
      if (response.success && response.data) {
        const stats = response.data;
        const hasRequiredFields = ['totalAssignments', 'activeAssignments', 'returnedAssets', 'utilizationRate'].every(
          field => typeof stats[field] === 'number'
        );
        
        if (hasRequiredFields) {
          this.testResults.push(this.createTestResult(
            testName,
            'pass',
            'Stats retrieved successfully',
            stats,
            duration
          ));
        } else {
          this.testResults.push(this.createTestResult(
            testName,
            'fail',
            'Missing required stats fields',
            stats,
            duration
          ));
        }
      } else {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'Invalid stats response',
          response,
          duration
        ));
      }
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Stats API call failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test API: Create Assignment
  async testCreateAssignment() {
    const testName = 'API: Create Assignment';
    const startTime = Date.now();
    
    try {
      const testAssignment = {
        assetId: 'AST-TEST-001',
        assetName: 'Test Asset',
        assetType: 'Test Equipment',
        assignedTo: 'Test User',
        assignedToId: 'test-user-id',
        assignedToEmail: 'test@example.com',
        department: 'IT',
        assignedDate: new Date().toISOString(),
        expectedReturnDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'Active',
        location: 'Test Location',
        purpose: 'Testing',
        condition: 'Good',
        notes: 'Test assignment for automated testing'
      };

      const response = await AssignmentService.createAssignment(testAssignment);
      const duration = Date.now() - startTime;
      
      if (response.success && response.data) {
        this.testResults.push(this.createTestResult(
          testName,
          'pass',
          'Assignment created successfully',
          { assignmentId: response.data.assignmentId },
          duration
        ));
        
        // Store created assignment ID for cleanup
        this.createdAssignmentId = response.data._id || response.data.assignmentId;
      } else {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'Failed to create assignment',
          response,
          duration
        ));
      }
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Create assignment failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test API: Update Assignment
  async testUpdateAssignment() {
    const testName = 'API: Update Assignment';
    const startTime = Date.now();
    
    try {
      if (!this.createdAssignmentId) {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'No assignment ID available for update test',
          null,
          Date.now() - startTime
        ));
        return;
      }

      const updateData = {
        location: 'Updated Test Location',
        notes: 'Updated test notes',
        condition: 'Excellent'
      };

      const response = await AssignmentService.updateAssignment(this.createdAssignmentId, updateData);
      const duration = Date.now() - startTime;
      
      if (response.success) {
        this.testResults.push(this.createTestResult(
          testName,
          'pass',
          'Assignment updated successfully',
          updateData,
          duration
        ));
      } else {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'Failed to update assignment',
          response,
          duration
        ));
      }
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Update assignment failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test API: Delete Assignment
  async testDeleteAssignment() {
    const testName = 'API: Delete Assignment';
    const startTime = Date.now();
    
    try {
      if (!this.createdAssignmentId) {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'No assignment ID available for delete test',
          null,
          Date.now() - startTime
        ));
        return;
      }

      const response = await AssignmentService.deleteAssignment(this.createdAssignmentId);
      const duration = Date.now() - startTime;
      
      if (response.success) {
        this.testResults.push(this.createTestResult(
          testName,
          'pass',
          'Assignment deleted successfully',
          null,
          duration
        ));
      } else {
        this.testResults.push(this.createTestResult(
          testName,
          'fail',
          'Failed to delete assignment',
          response,
          duration
        ));
      }
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Delete assignment failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test Form Validations
  async testFormValidations() {
    const testName = 'Form Validations';
    const startTime = Date.now();
    
    try {
      const validationTests = [
        {
          name: 'Required Fields',
          data: {},
          shouldFail: true,
          expectedErrors: ['assetId', 'assignedToId', 'department', 'assignedDate']
        },
        {
          name: 'Invalid Date Format',
          data: {
            assetId: 'AST-001',
            assignedToId: 'user-001',
            department: 'IT',
            assignedDate: 'invalid-date',
            expectedReturnDate: 'invalid-date'
          },
          shouldFail: true,
          expectedErrors: ['assignedDate', 'expectedReturnDate']
        },
        {
          name: 'Valid Data',
          data: {
            assetId: 'AST-001',
            assetName: 'Test Asset',
            assetType: 'Test',
            assignedTo: 'Test User',
            assignedToId: 'user-001',
            assignedToEmail: 'test@example.com',
            department: 'IT',
            assignedDate: new Date().toISOString(),
            expectedReturnDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'Active',
            location: 'Test Location',
            purpose: 'Testing',
            condition: 'Good'
          },
          shouldFail: false,
          expectedErrors: []
        }
      ];

      let passedValidations = 0;
      const validationResults = [];

      for (const test of validationTests) {
        const errors = this.validateAssignmentData(test.data);
        const hasExpectedErrors = test.expectedErrors.every(errorField => 
          errors.some(error => error.field === errorField)
        );
        
        if (test.shouldFail && hasExpectedErrors) {
          passedValidations++;
          validationResults.push({ test: test.name, status: 'pass', errors });
        } else if (!test.shouldFail && errors.length === 0) {
          passedValidations++;
          validationResults.push({ test: test.name, status: 'pass', errors });
        } else {
          validationResults.push({ test: test.name, status: 'fail', errors, expected: test.expectedErrors });
        }
      }

      const duration = Date.now() - startTime;
      this.testResults.push(this.createTestResult(
        testName,
        passedValidations === validationTests.length ? 'pass' : 'fail',
        `${passedValidations}/${validationTests.length} validation tests passed`,
        validationResults,
        duration
      ));
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Validation test failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Validate assignment data
  validateAssignmentData(data) {
    const errors = [];
    const requiredFields = ['assetId', 'assignedToId', 'department', 'assignedDate'];
    
    // Check required fields
    requiredFields.forEach(field => {
      if (!data[field] || data[field].toString().trim() === '') {
        errors.push({
          field,
          message: `${field} is required`,
          type: 'required'
        });
      }
    });

    // Validate dates
    if (data.assignedDate) {
      const assignedDate = new Date(data.assignedDate);
      if (isNaN(assignedDate.getTime())) {
        errors.push({
          field: 'assignedDate',
          message: 'Invalid assigned date format',
          type: 'format'
        });
      }
    }

    if (data.expectedReturnDate) {
      const returnDate = new Date(data.expectedReturnDate);
      if (isNaN(returnDate.getTime())) {
        errors.push({
          field: 'expectedReturnDate',
          message: 'Invalid expected return date format',
          type: 'format'
        });
      } else if (data.assignedDate) {
        const assignedDate = new Date(data.assignedDate);
        if (returnDate <= assignedDate) {
          errors.push({
            field: 'expectedReturnDate',
            message: 'Expected return date must be after assigned date',
            type: 'logic'
          });
        }
      }
    }

    // Validate email format
    if (data.assignedToEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.assignedToEmail)) {
        errors.push({
          field: 'assignedToEmail',
          message: 'Invalid email format',
          type: 'format'
        });
      }
    }

    return errors;
  }

  // Test Data Integrity
  async testDataIntegrity() {
    const testName = 'Data Integrity';
    const startTime = Date.now();
    
    try {
      const response = await AssignmentService.getAssignments();
      const assignments = response.data || [];
      
      let integrityIssues = [];
      
      assignments.forEach((assignment, index) => {
        // Check for required fields
        const requiredFields = ['assignmentId', 'assetId', 'assetName', 'assignedTo', 'department', 'assignedDate'];
        requiredFields.forEach(field => {
          if (!assignment[field]) {
            integrityIssues.push(`Assignment ${index}: Missing ${field}`);
          }
        });

        // Check date consistency
        if (assignment.assignedDate && assignment.expectedReturnDate) {
          const assignedDate = new Date(assignment.assignedDate);
          const returnDate = new Date(assignment.expectedReturnDate);
          if (returnDate <= assignedDate) {
            integrityIssues.push(`Assignment ${index}: Return date before assigned date`);
          }
        }

        // Check status consistency
        if (assignment.status === 'Returned' && !assignment.returnDate) {
          integrityIssues.push(`Assignment ${index}: Returned status but no return date`);
        }
      });

      const duration = Date.now() - startTime;
      this.testResults.push(this.createTestResult(
        testName,
        integrityIssues.length === 0 ? 'pass' : 'fail',
        integrityIssues.length === 0 ? 'All data integrity checks passed' : `${integrityIssues.length} integrity issues found`,
        integrityIssues,
        duration
      ));
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `Data integrity test failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Test UI Components
  async testUIComponents() {
    const testName = 'UI Components';
    const startTime = Date.now();
    
    try {
      const uiTests = [
        {
          name: 'KPI Cards',
          selector: '.page-kpi-card',
          expectedCount: 3
        },
        {
          name: 'Data Table',
          selector: '.table',
          expectedCount: 1
        },
        {
          name: 'Search Input',
          selector: 'input[type="text"]',
          expectedCount: 1
        },
        {
          name: 'Filter Dropdowns',
          selector: 'select',
          expectedCount: 2
        }
      ];

      const uiResults = [];
      let passedTests = 0;

      uiTests.forEach(test => {
        const elements = document.querySelectorAll(test.selector);
        const count = elements.length;
        const passed = count >= test.expectedCount;
        
        if (passed) passedTests++;
        
        uiResults.push({
          test: test.name,
          expected: test.expectedCount,
          actual: count,
          status: passed ? 'pass' : 'fail'
        });
      });

      const duration = Date.now() - startTime;
      this.testResults.push(this.createTestResult(
        testName,
        passedTests === uiTests.length ? 'pass' : 'fail',
        `${passedTests}/${uiTests.length} UI component tests passed`,
        uiResults,
        duration
      ));
    } catch (error) {
      this.testResults.push(this.createTestResult(
        testName,
        'error',
        `UI component test failed: ${error.message}`,
        null,
        Date.now() - startTime
      ));
    }
  }

  // Generate test report
  generateTestReport() {
    const report = {
      summary: {
        total: this.testResults.length,
        passed: this.testResults.filter(r => r.status === 'pass').length,
        failed: this.testResults.filter(r => r.status === 'fail').length,
        errors: this.testResults.filter(r => r.status === 'error').length,
        timestamp: new Date().toISOString()
      },
      results: this.testResults
    };

    return report;
  }

  // Export test results
  exportTestResults() {
    const report = this.generateTestReport();
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assignment-test-results-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export default new AssignmentTestService();
